import { CalendarMonthOutlined, MapOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { selectExternalInfos } from '@/domain/external/external.selectors';
import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { buildMapUrl } from '@/domain/position-geocode/utils/osm/build-osm-map-url';
import FairlyBackgroundLogo from '@/ui/assets/logo-fairly-background.png';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { formatDate } from '@/ui/utils/format-date';
import { truncateWithEllipsis } from '@/ui/utils/truncate-text';

export function ExternalFormInfos() {
  const infos = useAppSelector(selectExternalInfos);

  const expirationDate = infos.expirationDate;
  const eventPosition = infos.eventPosition;
  const title = infos.title;
  const { name, image, startDate } = infos.projectInfos;

  const formattedStartDate = startDate ? formatDate(startDate) : '';

  return (
    <Box>
      <Stack sx={{ p: 4 }}>
        <Typography sx={{ fontSize: '2rem', fontWeight: 'bold' }}>
          {translate('External.participatory-form')}
        </Typography>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          {name}
        </Typography>
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
          {formattedStartDate}
        </Typography>
        <DateCompo expirationDate={expirationDate} />
        <EventPositionCompo eventPosition={eventPosition} />
        <ImageCompo image={image} />
      </Stack>
    </Box>
  );
}

function DateCompo({ expirationDate }: { expirationDate: string | null }) {
  const isFurtherDate = () => {
    if (expirationDate) {
      const now = dayjs(expirationDate);
      return now.diff(new Date(), 'days') > 1;
    } else return false;
  };
  const getDaysLeft = () => {
    if (expirationDate) {
      const now = dayjs(expirationDate);
      return now.diff(new Date(), 'days');
    } else return '';
  };
  return (
    <>
      {isFurtherDate() && (
        <Typography
          sx={{
            my: 2,
            fontWeight: 'bold',
            color: ColorsRaw.scores.Mandarine,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CalendarMonthOutlined sx={{ mr: 1 }} />
          {translate('External.days-left-to-respond', {
            days: getDaysLeft(),
          })}
        </Typography>
      )}
    </>
  );
}

function EventPositionCompo({
  eventPosition,
}: {
  eventPosition: PositionGeocode | undefined;
}) {
  if (!eventPosition) {
    return <></>;
  }
  return (
    <Typography sx={{ my: 2, display: 'flex', alignItems: 'center' }}>
      <IconButton
        sx={{ ml: -0.5, mr: 0.5 }}
        size="small"
        href={buildMapUrl(eventPosition.coordinates)}
        target="_blank"
      >
        <MapOutlined />
      </IconButton>
      {truncateWithEllipsis(eventPosition.properties.name, 50)}
    </Typography>
  );
}

function ImageCompo({ image }: { image: string }) {
  return (
    <Box sx={{ my: 2 }}>
      {image ? (
        <img
          src={image}
          alt={translate('Projects.project-image')}
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            borderRadius: 10,
          }}
        />
      ) : (
        <img
          src={FairlyBackgroundLogo}
          alt={translate('Projects.project-image')}
          style={{
            padding: 50,
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            borderRadius: 10,
            backgroundColor: '#3E374D20',
          }}
        />
      )}
    </Box>
  );
}
